import { ChangeDetectionStrategy, Component, computed, ViewEncapsulation } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { WorkflowActionInputComponent } from "./workflow.action.input.component";
import { SystemActionComponent } from "./abstract.system.action.component";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { WorkflowContactRoleFieldRefsComponent } from "./workflow.contact.role.field.refs.component";

@Component({
    standalone: true,
    selector: "s25-ng-workflow-set-contact-role-action",
    template: `
        <div class="form-group">
            <label>Role</label>
            <s25-ng-dropdown-search-criteria
                [type]="'organizationRoles'"
                [value]="selectedRole()"
                (valueChange)="onRoleChange($event)"
                [placeholder]="'Select Role'"
            />
            <small class="form-text">Fallback role if not provided by form data</small>
        </div>

        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['organizationIdField']?.inputValue"
            (valueChange)="setInputValue('organizationIdField', $event)"
            [label]="'Organization ID Field'"
            [type]="'text'"
            [required]="false"
            [help]="
                'For forms without an implied organization, map a form field containing the organization ID (e.g., $CNET-123-ORG_ID). Forms with an implied organization automatically have organization ID available.'
            "
            [acceptedAnswerTypes]="acceptedFor('organizationIdField')"
        />

        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['contactIdField']?.inputValue"
            (valueChange)="setInputValue('contactIdField', $event)"
            [label]="'Contact ID Field'"
            [type]="'text'"
            [required]="false"
            [help]="
                'Map a form field containing the contact ID (e.g., $CNET-456-CONTACT_ID), or use the special value submitter to use the form submitter contact ID'
            "
            [acceptedAnswerTypes]="acceptedFor('contactIdField')"
        />

        <s25-ng-workflow-action-input
            class="system-input"
            [value]="inputMap()['roleIdField']?.inputValue"
            (valueChange)="setInputValue('roleIdField', $event)"
            [label]="'Role ID Field'"
            [type]="'text'"
            [required]="false"
            [help]="
                'Optional: Map a form field containing the role ID (e.g., $CNET-789-ROLE_ID). If not provided, the role selected above will be used.'
            "
            [acceptedAnswerTypes]="acceptedFor('roleIdField')"
        />

        <s25-ng-workflow-contact-role-field-refs
            [(inputs)]="inputs"
            (changed)="changed.emit()"
            [inputIdGenerator]="inputIdGenerator()"
        />
    `,
    styles: `
        .form-group {
            margin-bottom: 16px;
        }

        .form-group label {
            display: block;
            margin-bottom: 4px;
            font-weight: 500;
            color: #555;
        }

        .form-text {
            color: #666;
            font-size: 12px;
            margin-top: 4px;
            display: block;
        }

        .system-input {
            margin-bottom: 16px;
        }

        .system-input:last-child {
            margin-bottom: 0;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [
        FormsModule,
        WorkflowActionInputComponent,
        S25DropdownPaginatedModule,
        WorkflowContactRoleFieldRefsComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SetContactRoleActionComponent extends SystemActionComponent {
    protected readonly actionCode = "SET_CONTACT_ROLE" as const;
    readonly selectedRole = computed<DropDownItem | null>(() => {
        const roleIdInput = this.inputMap()["roleId"];
        if (!roleIdInput?.inputValue) return null;
        const roleId = parseInt(roleIdInput.inputValue, 10);
        if (isNaN(roleId)) return null;
        return {
            itemId: roleId,
            itemTypeId: undefined,
            itemName: "",
            txt: "",
            val: roleId.toString(),
        } as DropDownItem;
    });

    onRoleChange(role: DropDownItem | null) {
        if (role?.itemId) {
            this.setInputValue("roleId", role.itemId.toString());
        } else {
            this.setInputValue("roleId", "");
        }
    }
}
